import { useEffect, useLayoutEffect, useMemo, useState } from "react";

// Задаём внешний отступ от границ экрана, чтобы подсказка не прилипала
const VIEWPORT_PADDING = 8;
// Описываем поддерживаемые варианты расположения
type Placement = "top" | "bottom" | 'bottomRight' | "left" | "right";

interface IUseTooltipPoratal {
    // Храним рассчитанное положение по оси X
    positionLeft: number;
    // Храним рассчитанное положение по оси Y
    positionTop: number;
    // результирующее положение
    placementResult: Placement;
    // Флаг, что подсказка выходит за пределы экрана
    bodyOutsideView: boolean;
}

interface IUseTooltipPoratalProps {
    // Передаём ref на подсказку
    hintRef: React.MutableRefObject<HTMLDivElement | null>;
    // Передаём ref на триггер
    mainRef: React.MutableRefObject<HTMLDivElement | null>;
    // Флаг видимости подсказки
    _isOpen: boolean;
    // Принудительное открытие для первого рендера
    showFirst?: boolean;
    // Принудительное расположение слева
    left?: boolean;
    // Принудительное расположение справа
    right?: boolean;
    // Принудительное расположение справа
    bottom?: boolean;
    /** Отображение снизу по правому краю тела */
    bottomRight?: boolean;
}

export const useTooltipPoratal = ({
    hintRef,
    mainRef,
    _isOpen,
    showFirst,
    left,
    right,
    bottom,
    bottomRight,
}: IUseTooltipPoratalProps): IUseTooltipPoratal => {
    // Держим координату X подсказки
    const [positionLeft, setPositionLeft] = useState<number>(0);
    // Держим координату Y подсказки
    const [positionTop, setPositionTop] = useState<number>(0);
    // Держим координату Y подсказки
    const [placementResult, setPlacementResult] = useState<Placement>("top");
    const [bodyOutsideView, setBodyOutsideView] = useState<boolean>(false);
    // Мемоизируем предпочтительное расположение
    const preferredPlacement = useMemo<Placement>(() => {
        // Если явно указано слева, выбираем left
        if (left) return "left";
        // Если явно указано справа, выбираем right
        if (right) return "right";
        // Если явно указано справа, выбираем bottom
        if (bottom) return "bottom";
        // Если явно указано справа, выбираем bottomRight
        if (bottomRight) return 'bottomRight';
        // По умолчанию стараемся разместить сверху
        return "top";
    }, [left, right]);

    // Рассчитываем позицию и адаптируем под границы
    const calcPosition = () => {
        // Проверяем доступность dom-узлов
        if (!mainRef.current || !hintRef.current) return;
        // Получаем размеры триггера
        const targetRect = mainRef.current.getBoundingClientRect();
        // Получаем размеры подсказки
        const hintRect = hintRef.current.getBoundingClientRect();
        // Фиксируем ширину вьюпорта
        const viewportWidth = document.documentElement.clientWidth;
        // Фиксируем высоту вьюпорта
        const viewportHeight = document.documentElement.clientHeight;

        // Задаём список приоритетов для выбора позиции
        const basePlacements: Placement[] = ['top', 'bottom', 'right', 'left'];
        const placements: Placement[] = [
            preferredPlacement,
            ...basePlacements.filter(p => p !== preferredPlacement),
        ];

        // Вспомогательная функция, чтобы посчитать позицию для каждого варианта
        const computeForPlacement = (placement: Placement, originalPlacement?: Placement) => {
            // Инициализируем координаты
            let x = 0;
            // Инициализируем координаты
            let y = 0;
            // Проверяем выбранный вариант
            switch (placement) {
                case "top":
                    // Если исходное размещение было справа и стало top, выравниваем по правому краю триггера
                    if (originalPlacement === "right") {
                        x = targetRect.right - hintRect.width;
                    }
                    // Если исходное размещение было слева и стало top, выравниваем по левому краю триггера
                    else if (originalPlacement === "left") {
                        x = targetRect.left - 2;
                    }
                    // В остальных случаях центрируем по X над триггером
                    else {
                        x = targetRect.left + (targetRect.width / 2) - (hintRect.width / 2);
                    }
                    // Ставим подсказку над триггером
                    y = targetRect.top - hintRect.height - VIEWPORT_PADDING - 2;
                    // Возвращаем координаты
                    return { x, y, placement };
                case "bottom":
                    // Центрируем по X под триггером
                    x = targetRect.left + (targetRect.width / 2) - (hintRect.width / 2);
                    // Ставим подсказку под триггером
                    y = targetRect.bottom + VIEWPORT_PADDING;
                    // Возвращаем координаты
                    return { x, y, placement };
                case "bottomRight":
                    // Центрируем по X под триггером
                    x = targetRect.right - hintRect.width;
                    // Ставим подсказку под триггером
                    y = targetRect.bottom + VIEWPORT_PADDING;
                    // Возвращаем координаты
                    return { x, y, placement };
                case "left":
                    // Размещаем левее триггера
                    x = targetRect.left - hintRect.width - VIEWPORT_PADDING - 2;
                    // Выравниваем по центру триггера
                    y = targetRect.top + (targetRect.height / 2) - (hintRect.height / 2);
                    // Возвращаем координаты
                    return { x, y, placement };
                case "right":
                default:
                    // Размещаем правее триггера
                    x = targetRect.left + targetRect.width + VIEWPORT_PADDING + 2;
                    // Выравниваем по центру триггера
                    y = targetRect.top + (targetRect.height / 2) - (hintRect.height / 2);
                    // Возвращаем координаты
                    return { x, y, placement };
            }
        };

        // Проверяем, помещается ли выбранная позиция в экран
        const fitsInViewport = (candidate: { x: number; y: number; placement: Placement }) => {
            // Проверяем границу слева
            const fitsLeft = candidate.x >= VIEWPORT_PADDING;
            // Проверяем границу справа
            const fitsRight = candidate.x + hintRect.width <= viewportWidth - VIEWPORT_PADDING;
            // Проверяем границу сверху
            const fitsTop = candidate.y >= VIEWPORT_PADDING;
            // Проверяем границу снизу
            const fitsBottom = candidate.y + hintRect.height <= viewportHeight - VIEWPORT_PADDING;
            // Возвращаем результат
            return fitsLeft && fitsRight && fitsTop && fitsBottom;
        };

        // Подбираем лучшую позицию через поиск первого подходящего варианта
        // Передаем исходное размещение для корректного позиционирования при переключении на top
        const firstFit = placements.map(placement => computeForPlacement(placement, preferredPlacement)).find(fitsInViewport);
        // Фиксируем выбранную позицию: либо подходящую, либо первую по приоритету
        // Передаем исходное размещение для корректного позиционирования при переключении на top
        const selected = firstFit ?? computeForPlacement(placements[0], preferredPlacement);

        // Ограничиваем X в пределах экрана
        const clampedX = Math.min(
            Math.max(selected.x, VIEWPORT_PADDING),
            viewportWidth - hintRect.width - VIEWPORT_PADDING,
        );
        // Ограничиваем Y в пределах экрана
        const clampedY = Math.min(
            Math.max(selected.y, VIEWPORT_PADDING),
            viewportHeight - hintRect.height - VIEWPORT_PADDING,
        );

        // Обновляем координату X
        setPositionLeft(clampedX);
        // Обновляем координату Y
        setPositionTop(clampedY);

        setPlacementResult(selected.placement)
    };

    // Пересчитываем позицию при открытии и первом показе
    useLayoutEffect(() => {
        // Не пересчитываем, если подсказка скрыта
        if (!_isOpen && !showFirst) {
            // Явно возвращаем undefined, чтобы удовлетворить правило consistent-return
            return undefined;
        }
        // Выполняем расчет
        calcPosition();
        // Подписываемся на resize для актуальной позиции
        const handleResize = () => calcPosition();
        // Добавляем слушатель ресайза
        window.addEventListener("resize", handleResize);
        // Подписываемся на прокрутку страницы
        window.addEventListener("scroll", handleResize, true);
        // Очищаем обработчики
        return () => {
            // Удаляем обработчик ресайза
            window.removeEventListener("resize", handleResize);
            // Удаляем обработчик скролла
            window.removeEventListener("scroll", handleResize, true);
        };
        // Следим за видимостью и предпочтением позиции
    }, [_isOpen, showFirst, preferredPlacement]);

    // Отслеживаем видимость mainRef во viewport при showFirst
    useEffect(() => {
        // Если режим постоянного показа не активен — observer не нужен
        if (!showFirst) return undefined;
        // Если ref ещё не доступен — выходим
        if (!mainRef.current) return undefined;

        // Создаём IntersectionObserver
        const observer = new IntersectionObserver(
            ([entry]) => setBodyOutsideView(!entry.isIntersecting),
            {
                // Работаем относительно viewport
                root: null,
                // Минимальный порог — элемент хотя бы частично виден
                threshold: 0,
                // Отступы вокруг viewport: снизу отрабатывает на 100px раньше
                rootMargin: "0px 0px -80px 0px", // top right bottom left
            },
        );
        // Начинаем наблюдение за элементом
        observer.observe(mainRef.current);
        // Очищаем observer при размонтировании
        return () => observer.disconnect();
    }, [showFirst]);

    // Обновляем позицию при смене размеров подсказки или триггера
    useEffect(() => {
        // Не пересчитываем, если подсказка скрыта
        if (!_isOpen && !showFirst) return;
        // Выполняем расчет
        calcPosition();
        // Следим за зависимостями
    }, [hintRef.current?.offsetWidth, hintRef.current?.offsetHeight, mainRef.current?.offsetWidth, mainRef.current?.offsetHeight]);

    // Возвращаем рассчитанные данные
    return {
        positionLeft,
        positionTop,
        placementResult,
        bodyOutsideView,
    }
}